import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getCreditApplicationTemplate } from '../../../../redux/services/creditApplicationsTemplatesService';
import {
  AddressVerification,
  BankReference,
  ConditionalOptionValues,
  CreditApplication,
  CreditApplicationTemplate,
  DynamicInvitedSignaturesFields,
  GroupedCreditApplicationFields,
  InvitedSignatures,
  toCreditApplicationType,
  blockedBankReferenceEmailMessage,
  blockedTradeReferenceEmailMessage,
  findBlockedBankReferenceEmail,
  findBlockedTradeReferenceEmailDomain,
  getBlockedTradeReferenceEmailDomains,
  CreditApplicationType,
  getCreditApplicationTypeLabel,
} from '@dill/dill-shared';
import { ConditionalOptionPreviewRef } from '../CreditApplicationComponents/ConditionalOptionPreview';
import {
  createCreditApplication,
  getCreditApplication,
  getDraftCreditApplication,
  saveDraftCreditApplication,
  updateCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import {
  firebaseLargeFileUploadHelper,
  openDillToast,
  buildSupplierPath,
  isHTML,
} from '../../../../utils/helpers';
import * as Sentry from '@sentry/react';
import moment from 'moment';
import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import successChecksCircle from '../../../../assets/svg/success_checks_circle.svg';
import errorCircle from '../../../../assets/svg/error_circle.svg';
import { PreviewCreditApplicationData } from '../CreditApplicationComponents/PreviewCreditApplicationData';
import DynamicStepForm from '../CreditApplicationComponents/DynamicStepForm';
import { verifyAddressFieldsOnSubmit } from '../CreditApplicationComponents/verifyAddressFieldsOnSubmit';
import StepFormButtons from '../CreditApplicationComponents/StepFormButtons';
import { useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { AppSuccessModal } from '../../../general/AppSuccessModal/AppSuccessModal';
import { ProgressStepBar } from '../CreditApplicationComponents/ProgressStepBar';
import { decryptCreditApplicationData } from '../../../../utils/decryptCreditApplicationData';
import { useDecryption } from '../../../../utils/useDecryption';

type StateInvitedUser = {
  title: string;
  email: string;
  step: number;
  signatureGroupName?: string;
  placeHolder?: string;
  type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
  minimumCreditAmount?: number;
  fields: DynamicInvitedSignaturesFields[];
  minimumNumberOfSignatures?: number;
  signaturePageFieldIds?: string | null;
  id?: string | null;
  linkedFieldId?: string | null;
};

export const ManualAddApplication = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const parentRef = useRef<ConditionalOptionPreviewRef>(null);
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [fetchedCreditApplicationTemplate, setFetchedCreditApplicationTemplate] =
    useState<CreditApplicationTemplate | null>(null);
  const nextStep = () => setStep((prev) => Math.min(prev + 1, steps.length - 1));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 0));
  const [errors, setErrors] = useState<
    {
      uniqueId: string;
      errorText: string;
    }[]
  >([]);
  const [hasConditionalFieldErrors, setHasConditionalFieldErrors] = useState(false);
  const [allStepFieldsValues, setAllStepFieldsValues] = useState<
    {
      uniqueId: string;
      id: string;
      name: string;
      fromField:
        | 'TEXT'
        | 'TEXT_AREA'
        | 'NUMBER'
        | 'CURRENCY'
        | 'DATE'
        | 'CHECK_BOX'
        | 'OPTION_FIELD'
        | 'FILE'
        | 'DOCUMENT_FIELD'
        | 'INVITE_USER'
        | 'DATE'
        | 'CONDITIONAL_OPTION'
        | 'BANK_REFERENCE'
        | 'TRADE_REFERENCE'
        | 'CONDITIONAL_SELECTED_FIELD'
        | 'STATE_DROPDOWN'
        | 'CHECKBOX_LIST'
        | 'CONDITIONAL_FORM'
        | 'DYNAMIC_FORM_DOCUMENT'
        | 'AUTOFILL_CHECKBOX'
        | 'INTERNAL_SIGNER'
        | 'DROPDOWN';
      isRequired: boolean;
      isFieldEmail: boolean;
      isHashed: boolean;
      stepName: string;
      stepNumber: number;
      index: number;
      groupStepPosition: number;
      groupName: string;
      groupColumnType?: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
      isFieldArray: boolean;
      groupedPosition: number;
      value: string | number | boolean | File | any | null;
      optionType?: string;
      secondaryText?: string;
      status?:
        | 'Pending'
        | 'Requested'
        | 'Submitted'
        | 'Rejected'
        | 'Blocked'
        | 'Dropped'
        | 'Delivered'
        | 'Deferred'
        | 'Bounce';
      emailDate?: string;
      sendEmail?: boolean;
      type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      secondaryValue?: string | null;
      fieldValue?: ConditionalOptionValues[];
      formTemplate?: string | null;
      linkedFieldId?: string | null;
      currencyDecimalPlaces?: number;
      addressVerification?: AddressVerification | null;
      borderId?: string | null;
      copyFromFieldIds?: string | null;
      copyToFieldIds?: string | null;
      conditionalVisibility?: { sourceFieldId: string; sourceOptionValues: string[] } | null;
      validationType?: 'NONE' | 'EIN' | 'SSN' | 'PHONE';
      emailValidationType?: 'SINGLE' | 'MULTIPLE';
    }[]
  >([]);
  const [invitedSignatures, setInvitedSignatures] = useState<StateInvitedUser[]>([]);
  // Full, unfiltered list of invite slots from the template — used to re-add a slot to
  // `invitedSignatures` when its linked Yes/No field is answered "Yes" again.
  const [originalInvitedSignatures, setOriginalInvitedSignatures] = useState<StateInvitedUser[]>(
    []
  );
  const [invitedSignaturesErrors, setInvitedSignaturesErrors] = useState<
    { placeHolder: string; errorText: string }[]
  >([]);
  const [bankReference, setBankReference] = useState<BankReference[] | null>(null);
  const [bankError, setBankError] = useState<string | null>(null);
  const [creditApplicationId, setCreditApplicationId] = useState<string | null>(null);
  const creditApplicationIdRef = useRef<string | null>(null);
  const [isErrorCreatingAppOpen, setIsErrorCreatingAppOpen] = useState(false);
  const [isAddingCreatingAppSuccessModalOpen, setIsAddingCreatingAppSuccessModalOpen] =
    useState(false);
  const [domainDetails, setDomainDetails] = useState<{
    id: string;
    domainId: string;
    url: string;
    name: string;
    branches: { name: string; erpId?: string }[];
    salesReps: { name: string; erpId?: string; isHouseRep?: boolean }[];
  } | null>(null);

  const { creditApplicationTemplate } = useAppSelector((state) => state.creditApplicationTemplates);
  const { creditApplication } = useAppSelector((state) => state.creditApplications);
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);
  const { decryptValue } = useDecryption();

  const manuallyAddedBy = useMemo(
    () =>
      user
        ? {
            id: user.id,
            name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || undefined,
            email: user.email,
          }
        : undefined,
    [user]
  );

  const fetchDataUsingTemplateId = async (id: string) => {
    await dispatch(getCreditApplicationTemplate({ templateId: id }));
  };

  useEffect(() => {
    const pathlist = location.pathname.split('/');
    const templateId = pathlist[pathlist.length - 1];
    fetchDataUsingTemplateId(templateId);
  }, [location]);

  useEffect(() => {
    const basicSupplier = currentSupplier;
    const branches = supplierBranches
      .filter((branch) => branch?.notInCreditApp !== true)
      .map((branch) => ({ name: branch?.branchName ?? '', erpId: branch?.erpId ?? '' }));
    const seenSalesReps = new Set<string>();
    // Scoped to the application's own assigned branch when it has one — a rep from some other
    // branch isn't a valid choice here. Falls back to every branch's reps when unassigned.
    const salesReps = supplierBranches
      .filter((branch) => branch?.notInCreditApp !== true)
      .filter(
        (branch) => !creditApplication?.branchId || branch.branchId === creditApplication.branchId
      )
      .flatMap((branch) => branch?.salesRep ?? [])
      .filter((rep) => {
        const name = rep?.name ?? '';
        if (!name || seenSalesReps.has(name)) return false;
        seenSalesReps.add(name);
        return true;
      })
      .map((rep) => ({
        name: rep?.name ?? '',
        erpId: rep?.erpId ?? '',
        isHouseRep: rep?.isHouseRep ?? false,
      }));

    setDomainDetails({
      url: basicSupplier?.logo?.url ?? '',
      name: basicSupplier?.name ?? '',
      id: basicSupplier?.id ?? '',
      domainId: basicSupplier?.domainId ?? '',
      branches,
      salesReps,
    });
  }, [user, currentSupplier?.id, creditApplication?.branchId, supplierBranches]);

  const extractInviteUsers = (
    indx: number,
    whoNeedsToSign?: string,
    name?: string,
    type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR',
    minimumCreditAmount?: number,
    fields?: DynamicInvitedSignaturesFields[],
    minimumNumberOfSignatures?: number,
    isRequired?: boolean,
    signaturePageFieldIds?: string | null,
    id?: string,
    linkedFieldId?: string | null
  ): {
    title: string;
    email: string;
    step: number;
    signatureGroupName?: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    minimumCreditAmount?: number;
    fields: DynamicInvitedSignaturesFields[];
    minimumNumberOfSignatures?: number;
    isRequired?: boolean;
    signaturePageFieldIds?: string | null;
    id?: string;
    linkedFieldId?: string | null;
  }[] => {
    if (!whoNeedsToSign) return [];
    return whoNeedsToSign.split(',').map((option) => ({
      title: '',
      email: '',
      step: indx,
      signatureGroupName: name,
      placeHolder: option.trim(),
      type: type ?? 'AUTHORIZED_AGENT',
      minimumCreditAmount,
      fields: fields || [],
      minimumNumberOfSignatures,
      isRequired,
      id,
      ...(signaturePageFieldIds ? { signaturePageFieldIds } : {}),
      ...(linkedFieldId ? { linkedFieldId } : {}),
    }));
  };

  const applicationTitle = useMemo(() => {
    if (creditApplicationTemplate) {
      return getCreditApplicationTypeLabel(creditApplicationTemplate.applicationType, {
        long: true,
        formName: creditApplicationTemplate.name,
      });
    }
    return ' Application';
  }, [creditApplicationTemplate]);

  useEffect(() => {
    if (!creditApplicationTemplate) return;
    const inviteData: {
      title: string;
      email: string;
      step: number;
      fullNameNeeded?: boolean;
      driverLicenseNeeded?: boolean;
      signatureGroupName?: string;
      placeHolder?: string;
      type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      minimumCreditAmount?: number;
      fields: DynamicInvitedSignaturesFields[];
    }[] = [];

    creditApplicationTemplate.steps.forEach((stp, index) => {
      stp.fields?.forEach((field) => {
        if (field.stepType === 'NOT_GROUPED' && field.unGroupedFields) {
          field.unGroupedFields.forEach((f) => {
            if (f.fromField === 'INVITE_USER') {
              inviteData.push(
                ...extractInviteUsers(
                  index,
                  f.whoNeedsToSign,
                  f.name,
                  f.type,
                  f.minimumCreditAmount,
                  f.invitedSignaturesFields || [],
                  f.minimumNumberOfSignatures,
                  f.isRequired,
                  f.signaturePageFieldIds,
                  f?.id,
                  f?.linkedFieldId
                )
              );
            }
          });
        }

        if (field.stepType === 'GROUPED' && field.groupedFields) {
          field.groupedFields.forEach((group) => {
            group.fields.forEach((f) => {
              if (f.fromField === 'INVITE_USER') {
                inviteData.push(
                  ...extractInviteUsers(
                    index,
                    f.whoNeedsToSign,
                    f.name,
                    f.type,
                    f.minimumCreditAmount,
                    f.invitedSignaturesFields || [],
                    f.minimumNumberOfSignatures,
                    f.isRequired,
                    f.signaturePageFieldIds,
                    f?.id,
                    f?.linkedFieldId
                  )
                );
              }
            });
          });
        }
      });
    });

    setInvitedSignatures(inviteData);
    setOriginalInvitedSignatures(inviteData);
    setInvitedSignaturesErrors(
      inviteData.map((item) => ({ placeHolder: item?.placeHolder ?? '', errorText: '' }))
    );
  }, [creditApplicationTemplate]);

  useEffect(() => {
    if (creditApplicationId) {
      creditApplicationIdRef.current = creditApplicationId;
      dispatch(getCreditApplication({ creditApplicationId: creditApplicationId }));
    }
  }, [creditApplicationId]);

  const getDraft = async (email: string, applicationType?: CreditApplicationType) => {
    const result = await dispatch(getDraftCreditApplication({ email, applicationType }));
    if (result.type === 'creditApplications/getDraftCreditApplication/fulfilled') {
      const payload: any = result.payload;

      const creditApplicationsData: CreditApplication[] = payload.data;
      const currentSupplierId =
        fetchedCreditApplicationTemplate?.supplierId || domainDetails?.id || '';
      const byUpdatedAt = (a: CreditApplication, b: CreditApplication) =>
        new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime();

      const application = [...creditApplicationsData]
        .filter((app) => app.supplierId === currentSupplierId)
        .sort(byUpdatedAt)[0];

      if (application) {
        creditApplicationIdRef.current = application.id;

        const { approvalStatusHistory, applicationUpdateHistory, ...cleanedApplication } =
          application as any;

        const { decryptedBankReferences, decryptedFields } = await decryptCreditApplicationData(
          cleanedApplication,
          decryptValue
        );

        const filteredFields = decryptedFields.filter(
          (field) => field.id !== 'first-name-personal' && field.id !== 'last-name-personal'
        );
        setAllStepFieldsValues((prev) =>
          prev.map((f) => {
            const findVal = filteredFields.find((fld) => f.uniqueId === fld.uniqueId);
            if (!findVal) return f;

            return {
              ...f,
              value: findVal.value,
              ...(findVal.secondaryValue !== undefined
                ? { secondaryValue: findVal.secondaryValue }
                : {}),
              ...(findVal.fieldValue !== undefined ? { fieldValue: findVal.fieldValue } : {}),
            };
          })
        );

        if (decryptedBankReferences.length > 0) {
          setBankReference(decryptedBankReferences);
        }

        if (cleanedApplication.invitedSignatures) {
          setInvitedSignatures((prev) =>
            prev.map((f) => {
              const match = cleanedApplication?.invitedSignatures?.find(
                (invite: InvitedSignatures) =>
                  invite.placeHolder === f.placeHolder && invite.type === f.type
              );
              return match ? { ...f, ...match } : f;
            })
          );
        }
      }
    }
  };

  const duplicateWithNextGroupedPosition = (allFields: GroupedCreditApplicationFields[]) => {
    const groupedFields = allFields.filter((f) => f.groupedPosition !== undefined);

    // Find the current max grouped position
    const maxGroupPos = Math.max(...groupedFields.map((f) => f.groupedPosition ?? 0));

    // Get only fields of the last group
    const lastGroupFields = groupedFields.filter((f) => f.groupedPosition === maxGroupPos);

    const nextGroupedPosition = maxGroupPos + 1;

    const newGroup = lastGroupFields.map((field) => {
      // Strip existing `-group-X` suffix
      const cleanFieldId = field.id.replace(/-group-\d+$/, '');
      const clonedFields = field.fields.map((f) => {
        const cleanId = f.id.replace(/-group-\d+$/, '');
        return {
          ...f,
          id: `${cleanId}-group-${nextGroupedPosition}`,
        };
      });

      return {
        ...field,
        id: `${cleanFieldId}-group-${nextGroupedPosition}`,
        groupedPosition: nextGroupedPosition,
        fields: clonedFields,
      };
    });

    return [...allFields, ...newGroup];
  };

  // allStepFieldsValues strips INVITE_USER entries once a draft/application loads (see the
  // creditApplication effect below), so conditionalVisibility for an invite field must be read
  // straight from the template rather than from state.
  const findTemplateFieldConditionalVisibility = (
    id: string
  ): { sourceFieldId: string; sourceOptionValues: string[] } | null | undefined => {
    if (!fetchedCreditApplicationTemplate) return null;
    for (const st of fetchedCreditApplicationTemplate.steps) {
      for (const field of st.fields) {
        if (field.stepType === 'NOT_GROUPED' && field.unGroupedFields) {
          const found = field.unGroupedFields.find((f) => f.id === id);
          if (found) return found.conditionalVisibility;
        }
        if (field.stepType === 'GROUPED' && field.groupedFields) {
          for (const group of field.groupedFields) {
            const found = group.fields.find((f) => f.id === id);
            if (found) return found.conditionalVisibility;
          }
        }
      }
    }
    return null;
  };

  const getVisibleInvitedSignatures = (sigs: StateInvitedUser[]) =>
    sigs.filter((sig) => {
      if (!sig.id) return true;
      const conditionalVisibility = findTemplateFieldConditionalVisibility(sig.id);
      if (!conditionalVisibility?.sourceFieldId) return true;
      const { sourceFieldId, sourceOptionValues } = conditionalVisibility;
      const sourceValue = String(
        allStepFieldsValues.find((f) => f.id === sourceFieldId)?.value ?? ''
      );
      return sourceOptionValues.includes(sourceValue);
    });

  useEffect(() => {
    if (creditApplicationTemplate) {
      const updatedTemplate = {
        ...creditApplicationTemplate,
        steps: creditApplicationTemplate.steps.map((stepItem) => ({
          ...stepItem,
          fields: stepItem.fields.map((field) => {
            if (
              field.stepType === 'GROUPED' &&
              field.minimunNumberOfGroups &&
              field.groupedFields
            ) {
              const currentGroupCount = Math.max(
                ...field.groupedFields.map((item) => item.groupedPosition ?? 0)
              );
              const minimumGroups = field.minimunNumberOfGroups - 1;

              // If we have fewer groups than the minimum, add the missing ones
              if (currentGroupCount < minimumGroups) {
                const missingGroups = minimumGroups - currentGroupCount;
                let updatedGroupedFields = [...field.groupedFields];

                // Add the missing groups using the handleAddGroupedField logic
                for (let i = 0; i < missingGroups; i++) {
                  updatedGroupedFields = duplicateWithNextGroupedPosition(updatedGroupedFields);
                }

                return {
                  ...field,
                  groupedFields: updatedGroupedFields,
                };
              }
            }
            return field;
          }),
        })),
      };

      setFetchedCreditApplicationTemplate(updatedTemplate);
    }
  }, [creditApplicationTemplate]);

  useEffect(() => {
    if (
      fetchedCreditApplicationTemplate &&
      fetchedCreditApplicationTemplate.steps &&
      fetchedCreditApplicationTemplate.steps.length > 0
    ) {
      const templateSteps = fetchedCreditApplicationTemplate.steps.map((st) => st.name);
      templateSteps.push('Review & Submit');
      setSteps(templateSteps);

      const allStepFields = fetchedCreditApplicationTemplate.steps.flatMap((st, index) => {
        return st.fields.flatMap((field) => {
          if (field.stepType === 'NOT_GROUPED' && field.unGroupedFields) {
            return field.unGroupedFields.map((f) => {
              const uniqueId = `${st.name} ${field?.groupName} ${field.position} ${
                field.columnType
              } ${0} ${f.id || ''}`
                .toLowerCase()
                .replace(/\s+/g, '-');
              let value =
                f.fromField === 'TEXT' ||
                f.fromField === 'TEXT_AREA' ||
                f.fromField === 'OPTION_FIELD' ||
                f.fromField === 'NUMBER' ||
                f.fromField === 'CURRENCY'
                  ? ''
                  : f.fromField === 'CHECK_BOX' || f.fromField === 'AUTOFILL_CHECKBOX'
                  ? false
                  : null;
              const foundField = allStepFieldsValues.find((stFd) => stFd.uniqueId === uniqueId);
              const fieldValue = foundField?.fieldValue ?? null;
              if (foundField) {
                value = foundField.value;
              }
              if (f.fromField === 'DOCUMENT_FIELD') {
                value = f.documentText || '';
              }
              return {
                uniqueId: uniqueId,
                id: f.id || '',
                name: f.name || '',
                fromField: f.fromField || '',
                isRequired: f.isRequired || false,
                isHashed: f.isHashed || false,
                stepName: st.name,
                stepNumber: st.position || 0,
                groupStepPosition: field.position || 0,
                groupedPosition: 0,
                groupName: field?.groupName || '',
                groupColumnType: field.columnType || ('ONE_COLUMN' as const),
                isFieldArray: false,
                isFieldEmail: f.isFieldEmail || false,
                index: 0,
                value: value,
                ...(f.optionType ? { optionType: f.optionType } : {}),
                ...(f.secondaryText ? { secondaryText: f.secondaryText } : {}),
                ...(f.status ? { status: f.status } : {}),
                ...(f.emailDate ? { emailDate: f.emailDate } : {}),
                ...(field.sendEmail ? { sendEmail: field.sendEmail } : {}),
                ...(f.type ? { type: f.type } : {}),
                ...(fieldValue ? { fieldValue } : {}),
                ...(f?.formTemplate ? { formTemplate: f.formTemplate } : {}),
                ...(f?.linkedFieldId ? { linkedFieldId: f.linkedFieldId } : {}),
                ...((f as any).currencyDecimalPlaces !== undefined
                  ? { currencyDecimalPlaces: (f as any).currencyDecimalPlaces }
                  : {}),
                ...(f.borderId ? { borderId: f.borderId } : {}),
                ...(f.copyFromFieldIds ? { copyFromFieldIds: f.copyFromFieldIds } : {}),
                ...(f.copyToFieldIds ? { copyToFieldIds: f.copyToFieldIds } : {}),
                ...(f.conditionalVisibility
                  ? { conditionalVisibility: f.conditionalVisibility }
                  : {}),
                ...(f.validationType ? { validationType: f.validationType } : {}),
                ...(f.emailValidationType ? { emailValidationType: f.emailValidationType } : {}),
              };
            });
          }

          if (field.stepType === 'GROUPED' && field.groupedFields) {
            const groupFieldEntries = field.groupedFields.flatMap((group) =>
              group.fields.map((f) => {
                const uniqueId = `${st.name} ${field?.groupName} ${field.position} ${
                  field.columnType
                } ${group.groupedPosition} ${f.id || ''}`
                  .toLowerCase()
                  .replace(/\s+/g, '-');
                let value =
                  f.fromField === 'TEXT' ||
                  f.fromField === 'TEXT_AREA' ||
                  f.fromField === 'OPTION_FIELD' ||
                  f.fromField === 'NUMBER' ||
                  f.fromField === 'CURRENCY'
                    ? ''
                    : f.fromField === 'CHECK_BOX' || f.fromField === 'AUTOFILL_CHECKBOX'
                    ? false
                    : null;
                const foundField = allStepFieldsValues.find((stFd) => stFd.uniqueId === uniqueId);
                const fieldValue = foundField?.fieldValue ?? null;
                if (foundField) {
                  value = foundField.value;
                }
                if (f.fromField === 'DOCUMENT_FIELD') {
                  value = f.documentText || '';
                }
                return {
                  uniqueId: uniqueId,
                  id: f.id || '',
                  name: f.name || '',
                  fromField: f.fromField || '',
                  isRequired: f.isRequired || false,
                  isHashed: f.isHashed || false,
                  stepName: st.name,
                  stepNumber: st.position || 0,
                  groupStepPosition: field.position || 0,
                  groupedPosition: group.groupedPosition || 0,
                  groupName: field?.groupName || '',
                  groupColumnType: group.columnType || ('ONE_COLUMN' as const),
                  isFieldArray: field.isFieldArray || false,
                  isFieldEmail: f.isFieldEmail || false,
                  index: group.index || 0,
                  value: value,
                  ...(f.optionType ? { optionType: f.optionType } : {}),
                  ...(f.secondaryText ? { secondaryText: f.secondaryText } : {}),
                  ...(f.status ? { status: f.status } : {}),
                  ...(f.emailDate ? { emailDate: f.emailDate } : {}),
                  ...(field.sendEmail ? { sendEmail: field.sendEmail } : {}),
                  ...(f.type ? { type: f.type } : {}),
                  ...(fieldValue ? { fieldValue } : {}),
                  ...(f?.formTemplate ? { formTemplate: f.formTemplate } : {}),
                  ...(f?.linkedFieldId ? { linkedFieldId: f.linkedFieldId } : {}),
                  ...((f as any).currencyDecimalPlaces !== undefined
                    ? { currencyDecimalPlaces: (f as any).currencyDecimalPlaces }
                    : {}),
                  ...(f.borderId ? { borderId: f.borderId } : {}),
                  ...(f.copyFromFieldIds ? { copyFromFieldIds: f.copyFromFieldIds } : {}),
                  ...(f.copyToFieldIds ? { copyToFieldIds: f.copyToFieldIds } : {}),
                  ...(f.conditionalVisibility
                    ? { conditionalVisibility: f.conditionalVisibility }
                    : {}),
                  ...(f.validationType ? { validationType: f.validationType } : {}),
                  ...(f.emailValidationType ? { emailValidationType: f.emailValidationType } : {}),
                };
              })
            );

            if (field.hasTradeReferenceUpload) {
              const ocrUploadId = `${field.groupName}-ocr-upload`
                .toLowerCase()
                .replace(/\s+/g, '-');
              const ocrUniqueId =
                `${st.name} ${field?.groupName} ${field.position} ${field.columnType} 0 ocr-upload`
                  .toLowerCase()
                  .replace(/\s+/g, '-');
              const foundUploadField = allStepFieldsValues.find(
                (stFd) => stFd.uniqueId === ocrUniqueId
              );
              groupFieldEntries.push({
                uniqueId: ocrUniqueId,
                id: ocrUploadId,
                name: `${field?.groupName ?? ''} AI Upload`,
                fromField: 'TRADE_REFERENCE',
                isRequired: false,
                isHashed: false,
                stepName: st.name,
                stepNumber: st.position || 0,
                groupStepPosition: field.position || 0,
                groupedPosition: 0,
                groupName: field?.groupName || '',
                groupColumnType: 'ONE_COLUMN' as const,
                isFieldArray: field.isFieldArray || false,
                isFieldEmail: false,
                index: 0,
                value: foundUploadField?.value ?? null,
              });
            }

            return groupFieldEntries;
          }

          return [];
        });
      });
      setAllStepFieldsValues(allStepFields);
    }
  }, [fetchedCreditApplicationTemplate]);

  const onChangeFormData = ({
    id,
    value,
    secondaryValue,
    fieldValue,
    addressVerification,
  }: {
    id: string;
    value: any;
    secondaryValue?: any;
    fieldValue?: ConditionalOptionValues[];
    addressVerification?: AddressVerification | null;
  }) => {
    const currentField = allStepFieldsValues.find((f) => f.uniqueId === id);
    if (!currentField) return;

    const fieldUniqueId = currentField.id;

    let clearedErrorUniqueIds = [id];
    if (currentField.fromField === 'AUTOFILL_CHECKBOX') {
      const targetIds = (currentField.copyToFieldIds ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      clearedErrorUniqueIds = [
        id,
        ...allStepFieldsValues.filter((f) => targetIds.includes(f.id)).map((f) => f.uniqueId),
      ];
    }

    setAllStepFieldsValues((prev) => {
      const autofillValueMap: Record<string, any> = {};
      if (currentField.fromField === 'AUTOFILL_CHECKBOX') {
        const sourceIds = (currentField.copyFromFieldIds ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const targetIds = (currentField.copyToFieldIds ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        if (value === true) {
          targetIds.forEach((targetId, i) => {
            const sourceId = sourceIds[i] ?? sourceIds[0];
            if (sourceId) {
              autofillValueMap[targetId] = prev.find((f) => f.id === sourceId)?.value;
            }
          });
        } else {
          targetIds.forEach((targetId) => {
            const targetField = prev.find((f) => f.id === targetId);
            if (!targetField) return;
            autofillValueMap[targetId] =
              targetField.fromField === 'CHECK_BOX' || targetField.fromField === 'AUTOFILL_CHECKBOX'
                ? false
                : targetField.fromField === 'TEXT' ||
                  targetField.fromField === 'TEXT_AREA' ||
                  targetField.fromField === 'OPTION_FIELD' ||
                  targetField.fromField === 'NUMBER' ||
                  targetField.fromField === 'CURRENCY'
                ? ''
                : null;
          });
        }
      }

      return prev.map((f) => {
        if (f.uniqueId === id) {
          return {
            ...f,
            value: value,
            ...(secondaryValue !== undefined ? { secondaryValue: secondaryValue } : {}),
            ...(fieldValue !== undefined ? { fieldValue: fieldValue } : {}),
            ...(addressVerification !== undefined ? { addressVerification } : {}),
          };
        }

        if (f.id in autofillValueMap) {
          return { ...f, value: autofillValueMap[f.id] };
        }

        if (f.linkedFieldId?.includes(fieldUniqueId) && f.fromField !== 'DYNAMIC_FORM_DOCUMENT') {
          return { ...f, value };
        }

        if (f.fromField === 'DYNAMIC_FORM_DOCUMENT') {
          const fieldVal = (f.fieldValue ?? []).map((fv) => {
            if (fv.linkedFieldId?.includes(fieldUniqueId)) {
              return { ...fv, value };
            }
            return fv;
          });

          return { ...f, fieldValue: fieldVal };
        }

        return f;
      });
    });

    setErrors((prev) => prev.filter((f) => !clearedErrorUniqueIds.includes(f.uniqueId)));

    const foundInvitedSig = originalInvitedSignatures.find(
      (invSig) => invSig?.linkedFieldId && id.includes(invSig.linkedFieldId)
    );

    if (!foundInvitedSig) return;

    setInvitedSignatures((prev) => {
      const alreadyExists = prev.some((sig) => sig.placeHolder === foundInvitedSig.placeHolder);

      if (secondaryValue === 'Yes' && value === null) {
        if (!alreadyExists) {
          return [...prev, foundInvitedSig];
        }
        return prev;
      }

      return prev.filter((sig) => sig.placeHolder !== foundInvitedSig.placeHolder);
    });
  };

  useEffect(() => {
    if (
      !creditApplication ||
      !creditApplicationId ||
      !['SENT', 'OPENED', 'DRAFT'].includes(creditApplication.status)
    ) {
      return;
    }

    ['email-personal', 'first-name-personal', 'last-name-personal'].forEach((idPart) => {
      const foundField = creditApplication.fieldValues.find((f) => f.id.includes(idPart));
      const currentFieldValue = allStepFieldsValues.find(
        (f) => f.id.includes(idPart) && f.value === ''
      );
      if (foundField && currentFieldValue) {
        onChangeFormData({ id: foundField.uniqueId, value: foundField.value });
      }
    });
  }, [creditApplication, creditApplicationId]);

  const initializeCreditApp = async (bankRefs: BankReference[]) => {
    if (!bankRefs || bankRefs.length === 0) return;

    const decryptedBankRefs = await Promise.all(
      bankRefs.map(async (bankRef) => {
        const { approvalStatusHistory, ...bankRefWithoutHistory } = bankRef as any;
        const token = await decryptValue(bankRef.bankDetails.accountNumber, true);
        return {
          ...bankRefWithoutHistory,
          bankDetails: {
            ...bankRef.bankDetails,
            accountNumber: token,
          },
        };
      })
    );

    setBankReference(decryptedBankRefs);
  };

  useEffect(() => {
    if (!creditApplication || !creditApplicationId) return;

    const applyLoadedApplication = async () => {
      const { decryptedFields, decryptedInvitedSignatures } = await decryptCreditApplicationData(
        creditApplication,
        decryptValue
      );

      setAllStepFieldsValues((prev) =>
        prev.map((f) => {
          const fieldValue = decryptedFields.find((fv) => fv.uniqueId === f.uniqueId);

          if (!fieldValue) return f;

          if (f.fromField === 'CONDITIONAL_SELECTED_FIELD') {
            return { ...f, ...fieldValue };
          }

          if (f.value === '' || f.value === null || f.value === 0) {
            return { ...f, ...fieldValue };
          }

          return f;
        })
      );

      setAllStepFieldsValues((prev) =>
        prev
          .filter((f) => f.fromField !== 'INVITE_USER')
          .map((f) => {
            return f;
          })
      );

      if (creditApplication.bankReference) {
        initializeCreditApp(creditApplication.bankReference);
      }

      if (decryptedInvitedSignatures.length > 0) {
        setInvitedSignatures((prev) =>
          prev.map((f) => {
            const match = decryptedInvitedSignatures.find(
              (invite) => invite.placeHolder === f.placeHolder && invite.type === f.type
            );
            return match ? { ...f, ...match } : f;
          })
        );
      }
    };

    applyLoadedApplication();
  }, [creditApplication, creditApplicationId, decryptValue]);

  const validateForm = () => {
    const currentStepFields = allStepFieldsValues.filter(
      (f) => f.stepName === steps[step] && f.fromField !== 'BANK_REFERENCE'
    );

    const fieldErrors: { uniqueId: string; errorText: string }[] = [];

    for (const field of currentStepFields) {
      if (field.conditionalVisibility?.sourceFieldId) {
        const { sourceFieldId, sourceOptionValues } = field.conditionalVisibility;
        const sourceValue = String(
          allStepFieldsValues.find((f) => f.id === sourceFieldId)?.value ?? ''
        );
        if (!sourceOptionValues.includes(sourceValue)) continue;
      }

      // Required field check
      let value = field.value;
      if (typeof value === 'string') {
        value = value.trim();
      }
      if (
        field.isRequired &&
        (value === '' || value === null) &&
        field.fromField !== 'CONDITIONAL_FORM' &&
        field.fromField !== 'CONDITIONAL_SELECTED_FIELD'
      ) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} is required.`,
        });
      }

      if (field.isRequired && field.fromField === 'CONDITIONAL_FORM') {
        if (value === null && !field.secondaryValue) {
          fieldErrors.push({
            uniqueId: field.uniqueId,
            errorText: `${field.name} is required.`,
          });
        }
      }

      if (field.isRequired && field.fromField === 'CONDITIONAL_SELECTED_FIELD') {
        const hasValue = value !== null && value !== '';
        const hasFieldValue =
          field.fieldValue &&
          field.fieldValue.some(
            (fv) => fv.value !== null && fv.value !== '' && fv.value !== undefined
          );
        if (!hasValue && !hasFieldValue) {
          fieldErrors.push({
            uniqueId: field.uniqueId,
            errorText: isHTML(field.name) ? 'Field is required.' : `${field.name} is required.`,
          });
        }
      }

      // Email format validation
      else if (field.fromField === 'TEXT' && field.isFieldEmail && field.value) {
        const singleEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if ((field.emailValidationType ?? 'SINGLE') === 'MULTIPLE') {
          const emails = field.value
            .toString()
            .split(/[,;]/)
            .map((e: string) => e.trim())
            .filter(Boolean);
          const invalidEmail = emails.find((e: string) => !singleEmailRegex.test(e));
          if (invalidEmail) {
            fieldErrors.push({
              uniqueId: field.uniqueId,
              errorText: `"${invalidEmail}" is not a valid email address.`,
            });
          }
        } else if (!singleEmailRegex.test(field.value.toString())) {
          fieldErrors.push({
            uniqueId: field.uniqueId,
            errorText: `${field.name} must be a valid email.`,
          });
        }
      }

      // Tax ID / EIN format validation (XX-XXXXXXX)
      else if (
        field.fromField === 'TEXT' &&
        field.validationType === 'EIN' &&
        field.value &&
        !/^\d{2}-\d{7}$/.test(field.value.toString().trim())
      ) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} must be in XX-XXXXXXX format (e.g. 12-3456789).`,
        });
      }

      // SSN format validation (XXX-XX-XXXX)
      else if (
        field.fromField === 'TEXT' &&
        field.validationType === 'SSN' &&
        field.value &&
        !/^\d{3}-\d{2}-\d{4}$/.test(field.value.toString().trim())
      ) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} must be in XXX-XX-XXXX format (e.g. 123-45-6789).`,
        });
      }

      // Phone number format validation (XXX-XXX-XXXX)
      else if (
        field.fromField === 'TEXT' &&
        field.validationType === 'PHONE' &&
        field.value &&
        !/^\d{3}-\d{3}-\d{4}$/.test(field.value.toString().trim())
      ) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} must be in XXX-XXX-XXXX format (e.g. 555-123-4567).`,
        });
      }
    }

    // Trade reference email validations
    const personalEmail = allStepFieldsValues
      .find((f) => f.id === 'email-personal')
      ?.value?.toString()
      .trim()
      .toLowerCase();

    const tradeRefEmailFields = currentStepFields.filter(
      (f) => f.id.includes('trade-reference-company-email') && f.fromField === 'TEXT' && f.value
    );

    // Neither the supplier's own staff nor the applicant's own coworkers can vouch for the
    // applicant — see getBlockedTradeReferenceEmailDomains.
    const blockedTradeRefDomains = getBlockedTradeReferenceEmailDomains(currentSupplier, [
      personalEmail,
    ]);

    const seenTradeEmails: string[] = [];
    for (const field of tradeRefEmailFields) {
      const emailVal = field.value.toString().trim().toLowerCase();

      if (findBlockedTradeReferenceEmailDomain(emailVal, blockedTradeRefDomains)) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: blockedTradeReferenceEmailMessage(currentSupplier?.name),
        });
        continue;
      }

      if (personalEmail && emailVal === personalEmail) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: 'Trade reference email cannot be the same as your personal email.',
        });
        continue;
      }

      if (seenTradeEmails.includes(emailVal)) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText:
            'Duplicate trade reference email. Each trade reference must have a unique email.',
        });
      } else {
        seenTradeEmails.push(emailVal);
      }
    }

    setErrors(fieldErrors);
    return fieldErrors.length === 0;
  };

  const validateInviteSigners = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const foundInvitedSig = getVisibleInvitedSignatures(invitedSignatures);

    const foundMinimumCreditAmount =
      foundInvitedSig.find(
        (item) =>
          (item.minimumCreditAmount && item.minimumCreditAmount > 0 && item.type === 'GUARANTOR') ||
          item.type === 'CORPORATE_GUARANTOR'
      )?.minimumCreditAmount ?? 0;

    const foundAmount = allStepFieldsValues.find((f) => f.id.includes('amount-of'))?.value ?? 0;

    const validateEmail = (email: string, placeHolder: string, customMessage?: string) => {
      if (!email || email.trim() === '') {
        return customMessage || `${placeHolder} email is required.`;
      }
      if (!emailRegex.test(email)) {
        return `${placeHolder} email is not valid.`;
      }
      return '';
    };

    const validateByType = (item: StateInvitedUser) => {
      const signers = foundInvitedSig.filter((s) => s.type === item.type && s.email !== '');
      const invited = foundInvitedSig.filter((s) => s.type === item.type);

      const min = item.minimumNumberOfSignatures;
      const hasMinDefined = min !== undefined;
      const requiresAmountCheck =
        item.type === 'GUARANTOR' ||
        (item.type === 'CORPORATE_GUARANTOR' && foundAmount > foundMinimumCreditAmount);

      let errorText = '';

      if (hasMinDefined && min > signers.length && min !== invited.length) {
        errorText = validateEmail(
          item.email,
          item?.placeHolder ?? '',
          `At least ${min} ${item.type.toLowerCase().replace('_', ' ')} email is required.`
        );
      } else if (hasMinDefined && min > signers.length && min === invited.length) {
        errorText = validateEmail(item.email, item?.placeHolder ?? '');
      } else if (!hasMinDefined && requiresAmountCheck) {
        errorText = validateEmail(item.email, item?.placeHolder ?? '');
      } else if (item.email?.trim() !== '' && !emailRegex.test(item.email)) {
        errorText = `${item?.placeHolder ?? ''} email is not valid.`;
      }

      return errorText;
    };

    const inviteErrors = foundInvitedSig.map((item) => {
      let errorText = '';

      if (item.step === step) {
        errorText = validateByType(item);
      }

      return {
        placeHolder: item?.placeHolder ?? '',
        errorText,
      };
    });

    // Unique email validation (only current step)
    const typeMap: Record<string, Map<string, StateInvitedUser[]>> = {};

    foundInvitedSig
      .filter((item) => item.step === step && (item.email ?? '').trim() !== '')
      .forEach((item) => {
        if (!typeMap[item.type]) {
          typeMap[item.type] = new Map();
        }

        const emailMap = typeMap[item.type];
        const normalizedEmail = item.email.toLowerCase();

        if (!emailMap.has(normalizedEmail)) {
          emailMap.set(normalizedEmail, []);
        }

        emailMap.get(normalizedEmail)!.push(item);
      });

    const uniqueErrors: { placeHolder: string; errorText: string }[] = [];

    Object.values(typeMap).forEach((emailMap) => {
      emailMap.forEach((items) => {
        if (items.length > 1) {
          items.forEach((item) => {
            uniqueErrors.push({
              placeHolder: item.placeHolder ?? '',
              errorText: 'Email should not be the same.',
            });
          });
        }
      });
    });

    const mergedErrors = inviteErrors.map((err) => {
      const uniqueError = uniqueErrors.find((u) => u.placeHolder === err.placeHolder);

      return uniqueError && !err.errorText ? { ...err, errorText: uniqueError.errorText } : err;
    });

    setInvitedSignaturesErrors(mergedErrors);

    return mergedErrors.every((error) => error.errorText === '');
  };

  // Same rule as trade reference emails: a manual bank reference's contact can't be at the
  // supplier or at the applicant's own company — see getBlockedTradeReferenceEmailDomains.
  const blockedBankReferenceEmailDomains = getBlockedTradeReferenceEmailDomains(currentSupplier, [
    allStepFieldsValues
      .find((f) => f.id === 'email-personal')
      ?.value?.toString()
      .trim()
      .toLowerCase(),
  ]);

  const validateBankReferences = () => {
    const currentStepFields = allStepFieldsValues.filter((f) => f.stepName === steps[step]);
    const bankReferenceField = currentStepFields.find(
      (f) => f.fromField === 'BANK_REFERENCE' && f.isRequired
    );
    if (
      currentStepFields.some((f) => f.fromField === 'BANK_REFERENCE') &&
      findBlockedBankReferenceEmail(bankReference, blockedBankReferenceEmailDomains)
    ) {
      setBankError(blockedBankReferenceEmailMessage(currentSupplier?.name));
      return false;
    }
    if (bankReferenceField) {
      if (!bankReference) {
        setBankError('Bank reference is required.');
        return false;
      }
      setBankError(null);
      return true;
    }
    setBankError(null);
    return true;
  };

  const processDynamicFields = async (
    fields: DynamicInvitedSignaturesFields[],
    supplierId: string
  ) => {
    try {
      const processedFields = await Promise.all(
        fields.map(async (field) => {
          let value = field.value;

          // ---- FILE UPLOAD HANDLING ----
          if (value instanceof File) {
            const fileType = value.name.split('.').pop() || '';
            const storagePath = `creditApplicationFiles/${supplierId}/${Date.now()}`;

            try {
              const uploadUrl = await firebaseLargeFileUploadHelper({
                blobUrl: URL.createObjectURL(value),
                fileNameWithExtension: value.name,
                storagePath,
              });

              if (!uploadUrl) throw new Error(`Upload failed for file "${value.name}".`);

              value = { name: value.name, url: uploadUrl, fileType };
            } catch (error) {
              console.error(`❌ File upload error for "${value.name}":`, error);
              throw new Error(`Upload failed for file "${value.name}".`);
            }
          }

          // ---- HASHING ----
          if (field.isHashed) {
            const secretKey = process.env.REACT_APP_CRYPTO_KEY;
            if (secretKey) {
              value = CryptoJS.AES.encrypt(JSON.stringify(value), secretKey).toString();
            }
          }

          return { ...field, value };
        })
      );

      const baseInvitedSignatures = getVisibleInvitedSignatures(invitedSignatures).map((sig) => ({
        title: sig.title,
        email: sig.email,
        fields: sig.fields ?? [],
        signature: '',
        placeHolder: sig.placeHolder ?? '',
        type: sig.type ?? ('AUTHORIZED_AGENT' as const),
        ...(sig.minimumCreditAmount && { minimumCreditAmount: sig.minimumCreditAmount }),
        ...(sig.minimumNumberOfSignatures !== undefined && {
          minimumNumberOfSignatures: sig.minimumNumberOfSignatures,
        }),
        ...(sig.signaturePageFieldIds && { signaturePageFieldIds: sig.signaturePageFieldIds }),
      }));

      const email = allStepFieldsValues.find((f) => f.id === 'email-personal')?.value ?? '';

      const updatedSignatures = baseInvitedSignatures.map((sig) => {
        if (sig.email !== email) return sig;

        const mergedFields = sig.fields.map((field) => {
          const match = processedFields.find((pf) => field.id.includes(pf.id));
          return match ? { ...field, id: match.id, value: match.value } : field;
        });

        return {
          ...sig,
          fields: mergedFields,
        };
      });

      return updatedSignatures;
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      });
      return null;
    }
  };

  const signatureDetails = useMemo(() => {
    let email = '';
    const emailField = allStepFieldsValues.find((f) => f.id === 'email-personal')?.value;
    if (emailField) {
      email = emailField ?? '';
    }
    const updatedSignature = getVisibleInvitedSignatures(invitedSignatures).map((item) => ({
      title: item.title,
      email: item.email,
      fields: item.fields || [],
      signature: '',
      placeHolder: item.placeHolder || '',
      type: item.type || ('AUTHORIZED_AGENT' as const),
      ...(item.minimumCreditAmount ? { minimumCreditAmount: item.minimumCreditAmount } : {}),
      ...(item.minimumNumberOfSignatures !== undefined
        ? {
            minimumNumberOfSignatures: item.minimumNumberOfSignatures,
          }
        : {}),
      ...(item.signaturePageFieldIds ? { signaturePageFieldIds: item.signaturePageFieldIds } : {}),
    }));

    return { email, invitedSignatures: updatedSignature };
  }, [allStepFieldsValues, invitedSignatures]);

  const processData = async (
    dynamicFieldValues?: DynamicInvitedSignaturesFields[],
    fieldsOverride?: typeof allStepFieldsValues
  ) => {
    try {
      const secretKey = process.env.REACT_APP_CRYPTO_KEY;
      const fieldsToSubmit = fieldsOverride ?? allStepFieldsValues;

      const fieldValues = await Promise.all(
        fieldsToSubmit.map(async (f) => {
          let value = f.value;
          if (value instanceof File) {
            const fileType = value.name.split('.').pop() || '';
            try {
              const uploadFileUrl = await firebaseLargeFileUploadHelper({
                blobUrl: URL.createObjectURL(value),
                fileNameWithExtension: value.name,
                storagePath: `creditApplicationsFiles/${
                  creditApplicationTemplate?.supplierId || ''
                }/${Date.now()}`,
              });
              if (uploadFileUrl) {
                value = {
                  name: value.name,
                  url: uploadFileUrl ?? '',
                  fileType: fileType,
                };
              } else {
                throw new Error(`Upload failed for ${f.name}: "${value.name}". Please try again.`);
              }
            } catch (error) {
              Sentry.captureException(error);
              console.error(`❌ Failed to upload ${f.name}: "${value.name}":`, error);
              throw new Error(`Upload failed for ${f.name}: "${value.name}". Please try again.`);
            }
          }

          if (f.isHashed) {
            if (secretKey) {
              const token = CryptoJS.AES.encrypt(JSON.stringify(f.value), secretKey).toString();
              value = token;
            }
          }
          if (f.fromField === 'DATE' && f.value) {
            const formattedDate = moment(f.value).format('MM-DD-YYYY');
            value = formattedDate;
          }
          const fieldData: ConditionalOptionValues[] = [];
          if (f.fieldValue) {
            const fieldValue = await Promise.all(
              f.fieldValue.map(async (fv) => {
                let fvvalue = fv.value;
                if (fvvalue instanceof File) {
                  const fileType = fvvalue.name.split('.').pop() || '';
                  try {
                    const uploadFileUrl = await firebaseLargeFileUploadHelper({
                      blobUrl: URL.createObjectURL(fvvalue),
                      fileNameWithExtension: fvvalue.name,
                      storagePath: `creditApplicationFiles/${
                        creditApplicationTemplate?.supplierId || ''
                      }/${Date.now()}`,
                    });
                    if (uploadFileUrl) {
                      fvvalue = {
                        name: fvvalue.name,
                        url: uploadFileUrl ?? '',
                        fileType: fileType,
                      };
                    } else {
                      throw new Error(
                        `Upload failed for ${f.name}: "${value.name}". Please try again.`
                      );
                    }
                  } catch (error) {
                    Sentry.captureException(error);
                    console.error(`❌ Failed to upload ${f.name}: "${fvvalue.name}":`, error);
                    throw new Error(
                      `Upload failed for ${f.name}: "${fvvalue.name}". Please try again.`
                    );
                  }
                }

                if (fv.isHashed) {
                  if (secretKey) {
                    const token = CryptoJS.AES.encrypt(
                      JSON.stringify(fv.value),
                      secretKey
                    ).toString();
                    fvvalue = token;
                  }
                }
                if (fv.fromField === 'DATE' && fv.value) {
                  const formattedDate = moment(fv.value).format('MM-DD-YYYY');
                  fvvalue = formattedDate;
                }
                return {
                  ...fv,
                  value: fvvalue,
                };
              })
            );
            fieldData.push(...fieldValue);
          }
          return {
            uniqueId: f.uniqueId,
            id: f.id,
            name: f.name,
            fromField: f.fromField,
            isRequired: f.isRequired,
            isHashed: f.isHashed,
            stepName: f.stepName,
            stepNumber: f.stepNumber,
            groupStepPosition: f.groupStepPosition || 0,
            groupedPosition: f.groupedPosition || 0,
            groupName: f.groupName || '',
            groupColumnType: f.groupColumnType || ('ONE_COLUMN' as const),
            isFieldArray: f.isFieldArray || false,
            index: f.index || 0,
            isFieldEmail: f.isFieldEmail || false,
            value,
            ...(f.optionType ? { optionType: f.optionType } : {}),
            ...(f.secondaryText ? { secondaryText: f.secondaryText } : {}),
            ...(f.secondaryValue ? { secondaryValue: f.secondaryValue } : {}),
            ...(f.status ? { status: f.status } : {}),
            ...(f.emailDate ? { emailDate: f.emailDate } : {}),
            ...(f.sendEmail ? { sendEmail: f.sendEmail } : {}),
            ...(f.type ? { type: f.type } : {}),
            ...(f.fieldValue ? { fieldValue: fieldData } : {}),
            ...(f.currencyDecimalPlaces !== undefined
              ? { currencyDecimalPlaces: f.currencyDecimalPlaces }
              : {}),
            ...(f.addressVerification ? { addressVerification: f.addressVerification } : {}),
            ...(f.borderId ? { borderId: f.borderId } : {}),
            ...(f.formTemplate ? { formTemplate: f.formTemplate } : {}),
            ...(f.linkedFieldId ? { linkedFieldId: f.linkedFieldId } : {}),
          };
        })
      );
      let hashedBankReference = bankReference;
      if (secretKey && bankReference) {
        hashedBankReference = bankReference.map((bankRef) => {
          const token = CryptoJS.AES.encrypt(
            JSON.stringify(bankRef.bankDetails.accountNumber),
            secretKey
          ).toString();

          return {
            ...bankRef,
            bankDetails: {
              ...bankRef.bankDetails,
              accountNumber: token,
            },
          };
        });
      }
      let currentInvitedSignature = signatureDetails.invitedSignatures;

      const emailField = fieldsToSubmit.find((f) => f.id === 'email-personal')?.value;
      const checkSignatures = invitedSignatures.filter(
        (s) => s.email !== '' && s.email === emailField
      );
      if (
        emailField &&
        checkSignatures.length > 0 &&
        dynamicFieldValues &&
        dynamicFieldValues.length > 0
      ) {
        const processesField = await processDynamicFields(
          dynamicFieldValues,
          fetchedCreditApplicationTemplate?.supplierId ?? ''
        );
        if (!processesField) {
          return {
            data: null,
            message:
              'An unexpected error occurred during file upload or signature submission. Please try again.',
          };
        }
        currentInvitedSignature = processesField;
      }

      const data = {
        supplierId: fetchedCreditApplicationTemplate?.supplierId || '',
        templateId: fetchedCreditApplicationTemplate?.id || '',
        fieldValues,
        invitedSignatures: currentInvitedSignature,
        status: 'AWAITING_SIGNATURE' as const,
        applicationType:
          toCreditApplicationType(fetchedCreditApplicationTemplate?.applicationType) ||
          ('CREDIT_APPLICATION' as const),
        senderEmail: emailField ?? '',
        bankReference: hashedBankReference,
        manuallyAddedBy,
      };
      return { data, message: '' };
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      });
      return {
        data: null,
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      };
    }
  };

  const handleSubmitCreditApplication = async (
    dynamicFieldValues?: DynamicInvitedSignaturesFields[]
  ) => {
    setIsDisabled(false);
    setLoading(true);

    try {
      const verifiedFields = await verifyAddressFieldsOnSubmit(allStepFieldsValues);
      setAllStepFieldsValues(verifiedFields);

      const data = await processData(dynamicFieldValues, verifiedFields);
      if (!data.data) {
        dispatch(
          openMessageModal({
            buttonText: 'Close',
            modalType: 'ERROR',
            title: 'Error submitting application',
            subTitle: data.message || '',
            onClose: () => {},
          })
        );
        return;
      }

      const applicationId = creditApplicationId ?? creditApplicationIdRef.current;

      const stepData = {
        currentStep: {
          stepName: steps[step],
          stepNumber: Math.min(step + 1, steps.length - 1),
          totalSteps: steps.length - 1,
        },
      };

      const sharedFields = {
        ...(creditApplication?.assignedReviewerUserId && {
          assignedReviewerUserId: creditApplication.assignedReviewerUserId,
        }),
        ...(creditApplication?.amountAproved && {
          amountAproved: creditApplication.amountAproved,
        }),
        ...stepData,
      };

      const submitApplication = async () => {
        if (applicationId) {
          return dispatch(
            updateCreditApplication({
              creditApplicationId: applicationId,
              ...data.data,
              ...sharedFields,
            })
          );
        }

        return dispatch(createCreditApplication({ ...data.data, ...sharedFields }));
      };

      const response = await submitApplication();
      const isFulfilled = response.type.includes('/fulfilled');

      if (isFulfilled) {
        setIsAddingCreatingAppSuccessModalOpen(true);
      } else {
        setIsErrorCreatingAppOpen(true);
      }
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const checkDraft = async () => {
    const email = allStepFieldsValues.find(
      (f) => f.fromField === 'TEXT' && f.id === 'email-personal'
    )?.value;

    if (email) {
      await getDraft(
        email,
        toCreditApplicationType(fetchedCreditApplicationTemplate?.applicationType)
      );
    }
  };

  const handleValidateConditionalSelectedField = () => {
    if (!parentRef.current) {
      setHasConditionalFieldErrors(false);
      return true;
    }
    // Side effect only (renders any inline sub-field errors) — its return value
    // isn't trustworthy enough to gate navigation, so it doesn't factor into isValid.
    parentRef.current.validate();

    const currentStepFields = allStepFieldsValues
      .filter((f) => f.stepName === steps[step] && f.fieldValue && f.fromField !== 'BANK_REFERENCE')
      .flatMap((f) => f.fieldValue);

    const hasInvalidFields = currentStepFields.some((field) => {
      if (!field) return false;
      if (field.hidden) return false;

      if (
        field.isRequired &&
        ((typeof field.value === 'string' && field.value.trim() === '') || field.value === null)
      ) {
        return true;
      }

      if (
        field.isFieldEmail &&
        field.fromField === 'TEXT' &&
        typeof field.value === 'string' &&
        field.value.trim() !== ''
      ) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if ((field.emailValidationType ?? 'SINGLE') === 'MULTIPLE') {
          const emails = field.value
            .split(/[,;]/)
            .map((e) => e.trim())
            .filter(Boolean);
          return emails.some((e) => !emailRegex.test(e));
        }
        return !emailRegex.test(field.value);
      }

      if (
        field.validationType === 'EIN' &&
        field.fromField === 'TEXT' &&
        typeof field.value === 'string' &&
        field.value.trim() !== ''
      ) {
        return !/^\d{2}-\d{7}$/.test(field.value.trim());
      }

      if (
        field.validationType === 'SSN' &&
        field.fromField === 'TEXT' &&
        typeof field.value === 'string' &&
        field.value.trim() !== ''
      ) {
        return !/^\d{3}-\d{2}-\d{4}$/.test(field.value.trim());
      }

      if (
        field.validationType === 'PHONE' &&
        field.fromField === 'TEXT' &&
        typeof field.value === 'string' &&
        field.value.trim() !== ''
      ) {
        return !/^\d{3}-\d{3}-\d{4}$/.test(field.value.trim());
      }

      return false;
    });

    const isValid = !hasInvalidFields;
    setHasConditionalFieldErrors(!isValid);
    return isValid;
  };

  const handleSaveDraft = async () => {
    try {
      console.log('Starting save draft...');

      const { data } = await processData();
      if (!data) return;

      const stepData = {
        currentStep: {
          stepName: steps[step],
          stepNumber: Math.min(step + 1, steps.length - 1),
          totalSteps: steps.length - 1,
        },
      };

      const saveDraft = () =>
        dispatch(
          saveDraftCreditApplication({
            creditApplicationId: creditApplicationIdRef.current,
            creditApplicationDetails: {
              ...data,
              ...stepData,
              status: 'DRAFT' as const,
            },
          })
        );

      const result = await saveDraft();

      if (result.type === 'creditApplications/saveDraftCreditApplication/fulfilled') {
        const payload = result.payload as any;
        const id = payload?.data?.id;

        if (id && !creditApplicationIdRef.current) {
          creditApplicationIdRef.current = id;
          setCreditApplicationId(id);
        }
      } else {
        console.error('Failed to save draft:', result.payload);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      console.log('Save draft complete.');
    }
  };

  const handleNextStep = async () => {
    if (!handleValidateConditionalSelectedField()) {
      return;
    }

    if (!validateForm()) {
      return;
    }
    if (!validateInviteSigners()) return;

    if (!validateBankReferences()) return;

    setLoading(true);
    if (step === 0) {
      await checkDraft();
    }

    if (step > 0 && step < steps.length - 1) {
      handleSaveDraft();
    }

    setLoading(false);
    if (step === steps.length - 1) {
      const inviteEmails = Array.from(
        new Set(
          getVisibleInvitedSignatures(invitedSignatures)
            .map((sign) => sign.email?.toLowerCase().trim())
            .filter(Boolean)
        )
      );
      dispatch(
        openMessageModal({
          modalType: 'INFO',
          title: 'Confirm Submission',
          subTitle: `Are you sure you want to submit this ${applicationTitle.toLowerCase()} request? ${
            inviteEmails.length > 0
              ? `Upon submission, we would send emails to the list of invitees for signing: ${inviteEmails.join(
                  ', '
                )}`
              : ''
          }`,
          buttonText: 'Close',
          isSecondButton: true,
          secondButtonText: 'Submit',
          onSecondButtonClick: async () => {
            handleSubmitCreditApplication();
          },
        })
      );
      return;
    }

    nextStep();
    setErrors([]);
  };

  const handleAddGroupedField = ({
    groupName,
    position,
  }: {
    groupName: string;
    position: number;
  }) => {
    if (!fetchedCreditApplicationTemplate) return;

    const currentStepFields = fetchedCreditApplicationTemplate.steps[step]?.fields || [];

    const groupedFields =
      currentStepFields.find((f) => f.groupName === groupName && f.position === position)
        ?.groupedFields || [];

    if (!groupedFields) return;

    const newGroupedFields = duplicateWithNextGroupedPosition(groupedFields);
    setFetchedCreditApplicationTemplate((prev) => {
      if (!prev) return null;
      const updatedSteps = prev.steps.map((st) => {
        if (st.name === steps[step]) {
          return {
            ...st,
            fields: st.fields.map((f) => {
              if (f.groupName === groupName && f.position === position) {
                return {
                  ...f,
                  groupedFields: newGroupedFields,
                };
              }
              return f;
            }),
          };
        }
        return st;
      });
      return { ...prev, steps: updatedSteps };
    });
  };

  const handleRemoveGroupedField = ({
    groupName,
    position,
    groupPostion,
  }: {
    groupName: string;
    position: number;
    groupPostion: number;
  }) => {
    if (!fetchedCreditApplicationTemplate) return;

    const currentStepFields = fetchedCreditApplicationTemplate.steps[step]?.fields || [];

    const groupedFields =
      currentStepFields.find((f) => f.groupName === groupName && f.position === position)
        ?.groupedFields || [];

    if (!groupedFields) return;

    const newGroupedFields = groupedFields.filter((f) => f.groupedPosition !== groupPostion);

    setFetchedCreditApplicationTemplate((prev) => {
      if (!prev) return null;
      const updatedSteps = prev.steps.map((st) => {
        if (st.name === steps[step]) {
          return {
            ...st,
            fields: st.fields.map((f) => {
              if (f.groupName === groupName && f.position === position) {
                return {
                  ...f,
                  groupedFields: newGroupedFields,
                };
              }
              return f;
            }),
          };
        }
        return st;
      });
      return { ...prev, steps: updatedSteps };
    });
  };

  const handleUpdateInviteSigners = (
    data: {
      title: string;
      email: string;
      step: number;
      signatureGroupName?: string;
      placeHolder?: string;
      type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      minimumCreditAmount?: number;
      fields: DynamicInvitedSignaturesFields[];
      minimumNumberOfSignatures?: number;
    }[]
  ) => {
    setInvitedSignatures(data);
    setInvitedSignaturesErrors(
      data.map((item) => ({ placeHolder: item?.placeHolder ?? '', errorText: '' }))
    );
  };

  const handleBankReferenceChange = (
    updater: BankReference[] | null | ((prev: BankReference[] | null) => BankReference[] | null)
  ) => {
    setBankReference((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  };

  const amountOfCredit = useMemo(() => {
    const amount = allStepFieldsValues.find((f) => f.id.includes('amount-of'))?.value;
    return amount && typeof amount === 'number' ? amount : undefined;
  }, [allStepFieldsValues]);

  return (
    <div className="w-full h-full flex  p-4 gap-4">
      <AppSuccessModal
        open={isAddingCreatingAppSuccessModalOpen}
        modalImage={successChecksCircle}
        highlightedText={``}
        title={`${applicationTitle} added!`}
        subTitle={`This ${applicationTitle.toLowerCase()} has been successfully added to your account.`}
        buttonText={'View Applications'}
        handleClose={() => {
          setIsAddingCreatingAppSuccessModalOpen(false);
          navigate(buildSupplierPath(currentSupplier?.id, `/creditApplications`));
        }}
      />
      <AppSuccessModal
        open={isErrorCreatingAppOpen}
        modalImage={errorCircle}
        title="Adding your application failed!"
        subTitle={`Failed to add your ${applicationTitle.toLowerCase()}. Contact customer support`}
        buttonText="Close"
        secondButtonText="Retry"
        isSecondButton={true}
        onSecondButtonClick={() => {
          setIsErrorCreatingAppOpen(false);
          handleSubmitCreditApplication();
        }}
        handleClose={() => {
          setIsErrorCreatingAppOpen(false);
        }}
      />
      <div className="w-full WHITE-BG p-4 rounded-lg">
        <div className="p-3 w-full text-xl font-semibold">{applicationTitle}</div>
        <div className="h-[calc(100vh-200px)] overflow-y-scroll w-full flex flex-col">
          <div className="p-3 w-full">
            <ProgressStepBar currentStep={step} steps={steps} />
          </div>
          <div>
            {step === steps.length - 1 ? (
              <div>
                <h2 className="text-base md:text-lg font-semibold mb-4">Review & Submit</h2>
                <PreviewCreditApplicationData
                  formData={allStepFieldsValues}
                  bankReference={bankReference}
                  isRequest={true}
                  inviteSignatures={invitedSignatures.map((item) => ({
                    ...item,
                    signature: '',
                    placeHolder: item.placeHolder ?? '',
                  }))}
                />
              </div>
            ) : (
              <DynamicStepForm
                ref={parentRef}
                title={fetchedCreditApplicationTemplate?.steps[step]?.name ?? ''}
                stepFields={fetchedCreditApplicationTemplate?.steps[step]?.fields || []}
                formData={allStepFieldsValues}
                onChange={onChangeFormData}
                enableAutofillCheckboxes
                errors={errors}
                onAddGroupedField={handleAddGroupedField}
                onRemoveGroupedField={handleRemoveGroupedField}
                onUpdateInviteSigners={handleUpdateInviteSigners}
                invitedSignatures={invitedSignatures}
                invitedSignaturesErrors={invitedSignaturesErrors}
                bankReference={bankReference}
                onUpdateBankReference={handleBankReferenceChange}
                blockedBankReferenceEmailDomains={blockedBankReferenceEmailDomains}
                bankError={bankError}
                bankReferenceRequiredFields={
                  fetchedCreditApplicationTemplate?.bankReferenceRequiredFields || []
                }
                amountOfCredit={amountOfCredit}
                domainDetails={domainDetails}
                step={step}
              />
            )}
            <StepFormButtons
              step={step}
              minStep={0}
              maxStep={steps.length - 1}
              onPrevious={prevStep}
              onContinue={handleNextStep}
              isLoading={loading}
              isDisabled={isDisabled}
              error={
                errors.length > 0 ||
                invitedSignaturesErrors.some((e) => e.errorText !== '') ||
                !!bankError ||
                hasConditionalFieldErrors
                  ? 'Please fill in all required fields before continuing.'
                  : null
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
